import Domain from './models/Domain';

const extractId = o => Object.keys(o)[0];

export default class ThingsService {
    /* @ngInject */
    constructor($q, AxiosService, AuthService) {
        this.$q = $q;
        this.AxiosService = AxiosService;
        this.AuthService = AuthService;

        this.selectedDomain = undefined;
    }

    getThings() {
        const query = {
            size: 5000,
            from: 0,
            sort: { thingName: { order: 'asc' } },
            query: { match_all: {} },
        };

        if (this.selectedDomain) {
            query.query.bool = {
                filter: {
                    bool: {
                        should: [{ term: { domain: this.selectedDomain } }],
                    },
                },
            };
        }

        return this.AxiosService.http
            .post(
                'things/find',
                { query },
                { headers: { authorization: this.AuthService.getToken() } },
            )
            .then(response => response.data);
    }

    getThing(thingId) {
        return this.$q((resolve, reject) => {
            this.AxiosService.http
                .get('things', {
                    params: { thingName: thingId },
                    headers: { authorization: this.AuthService.getToken() },
                })
                .then(response => response.data)
                .then(data => resolve(data))
                .catch(error => reject(error));
        });
    }

    updateThing({ thingId, label, domain } = {}) {
        return this.$q((resolve, reject) => {
            this.AxiosService.http
                .patch(
                    `things/${thingId}`,
                    { label, domain },
                    { headers: { authorization: this.AuthService.getToken() } },
                )
                .then(response => response.data)
                .then(data => resolve(data))
                .catch(error => reject(error));
        });
    }

    /**
     * Returns a promise that might resolve into a list
     * of Domain objects.
     */
    getDomains() {
        return this.$q((resolve, reject) => {
            this.AxiosService.http
                .get('domains', {
                    headers: { authorization: this.AuthService.getToken() },
                })
                .then(response => response.data)
                .then((data) => {
                    const rootId = extractId(data);
                    const rootData = data[rootId];
                    const domain = new Domain(rootId, rootData);
                    resolve(domain);
                })
                .catch(error => reject(error));
        });
    }

    /**
     *
     * @param {{
     *   fromDate: Date;
     *   toDate: Date;
     *   thingId: string;
     * }} params
     *
     * @returns
     */
    getObservations(params) {
        return this.$q((resolve, reject) => {
            let aggregationResolution = '';
            const diff = params.toDate - params.fromDate;
            const aDay = 24 * 3600 * 1000;
            const aWeek = 7 * 24 * 3600 * 1000;

            if (diff <= aDay) {
                aggregationResolution = '1s';
            } else if (diff > aDay && diff <= aWeek) {
                aggregationResolution = '1m';
            } else {
                aggregationResolution = '30m';
            }

            const query = {
                size: 1,
                trackScores: false,
                query: {
                    bool: {
                        must: [
                            {
                                range: {
                                    timestamp: {
                                        gte: params.fromDate.valueOf(),
                                        lte: params.toDate.valueOf(),
                                    },
                                },
                            },
                            { term: { thingName: params.thingId } },
                        ],
                    },
                },
                aggs: {
                    hist: {
                        date_histogram: {
                            field: 'timestamp',
                            interval: aggregationResolution,
                            time_zone: '+01:00',
                            min_doc_count: 1,
                            extended_bounds: {
                                min: params.fromDate.valueOf(),
                                max: params.toDate.valueOf(),
                            },
                            order: { _key: 'asc' },
                        },
                        aggs: {
                            box_temperature: {
                                max: { field: 'state.box_temperature' },
                            },
                            ambient_temperature: {
                                max: { field: 'state.ambient_temperature' },
                            },
                            door: {
                                max: { field: 'state.door_open' },
                            },
                            power: {
                                max: { field: 'state.mains_powered' },
                            },
                            moving: {
                                max: { field: 'state.moving' },
                            },
                            latlng: {
                                geo_centroid: { field: 'state.latlng' },
                            },
                        },
                    },
                },
            };

            this.AxiosService.http
                .post(
                    'observations/find',
                    { query },
                    { headers: { authorization: this.AuthService.getToken() } },
                )
                .then(response => response.data)
                .then(data => resolve(data))
                .catch(error => reject(error));
        });
    }

    /**
     *
     * @param {{
     *   fromDate: Date;
     *   toDate: Date;
     *   thingId: string;
     * }} params
     *
     * @returns
     */
    getRawObservations(params) {
        return this.$q((resolve, reject) => {
            const query = {
                size: 5000,
                _source: [
                    'state.mains_powered',
                    'state.moving',
                    'state.door_open',
                    'state.box_temperature',
                    'state.ambient_temperature',
                    'state.latlng',
                    'timestamp',
                    'state.Virtual_In_Transport',
                ],
                sort: {
                    timestamp: {
                        order: 'asc',
                    },
                },
                query: {
                    bool: {
                        filter: {
                            bool: {
                                must: [
                                    { term: { thingName: params.thingId } },
                                    {
                                        range: {
                                            timestamp: {
                                                gte: params.fromDate.valueOf(),
                                                lte: params.toDate.valueOf(),
                                            },
                                        },
                                    },
                                ],
                                // minimum_should_match: 1,
                                should: [
                                    {
                                        exists: { field: 'state.mains_powered' },
                                    },
                                    {
                                        exists: { field: 'state.moving' },
                                    },
                                    {
                                        exists: { field: 'state.door_open' },
                                    },
                                    {
                                        exists: { field: 'state.box_temperature' },
                                    },
                                    {
                                        exists: { field: 'state.ambient_temperature' },
                                    },
                                    {
                                        exists: { field: 'state.latlng' },
                                    },
                                    {
                                        exists: { field: 'state.Virtual_In_Transport' },
                                    },
                                ],
                            },
                        },
                    },
                },
            };

            this.AxiosService.http
                .post(
                    'observations/find',
                    { query },
                    { headers: { authorization: this.AuthService.getToken() } },
                )
                .then(response => response.data)
                .then(data => resolve(data))
                .catch(error => reject(error));
        });
    }

    /**
     *
     * @param {{
     *   fromDate: Date;
     *   toDate: Date;
     *   thingId: string;
     * }} params
     *
     * @returns
     */
    getRawHACCPObservations(params) {
        return this.$q((resolve, reject) => {
            const query = {
                size: 5000,
                _source: [
                    'state.mains_powered',
                    'state.moving',
                    'state.door_open',
                    'state.box_temperature',
                    'state.ambient_temperature',
                    'state.latlng',
                    'timestamp',

                    'state.rd_state',
                    'state.Virtual_In_Transport',
                ],
                sort: {
                    timestamp: {
                        order: 'asc',
                    },
                },
                query: {
                    bool: {
                        filter: {
                            bool: {
                                must: [
                                    { term: { thingName: params.thingId } },
                                    {
                                        range: {
                                            timestamp: {
                                                gte: params.fromDate.valueOf(),
                                                lte: params.toDate.valueOf(),
                                            },
                                        },
                                    },
                                ],
                                // minimum_should_match: 1,
                                should: [
                                    {
                                        exists: { field: 'state.rd_state' },
                                    },
                                    {
                                        exists: { field: 'state.mains_powered' },
                                    },
                                    {
                                        exists: { field: 'state.moving' },
                                    },
                                    {
                                        exists: { field: 'state.door_open' },
                                    },
                                    {
                                        exists: { field: 'state.box_temperature' },
                                    },
                                    {
                                        exists: { field: 'state.ambient_temperature' },
                                    },
                                    {
                                        exists: { field: 'state.latlng' },
                                    },
                                    {
                                        exists: { field: 'state.Virtual_In_Transport' },
                                    },
                                ],
                            },
                        },
                    },
                },
            };

            this.AxiosService.http
                .post(
                    'observations/find',
                    { query },
                    { headers: { authorization: this.AuthService.getToken() } },
                )
                .then(response => response.data)
                .then(data => resolve(data))
                .catch(error => reject(error));
        });
    }
}



// WEBPACK FOOTER //
// ./src/app/services/things.service.js